import React from 'react';
import { reportError } from '../utils/errorReporting';
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunkLoadError';

// Without this boundary, any render error unmounts the entire React tree and
// leaves the customer staring at a blank page (React 18 behavior). This keeps
// the failure contained and recoverable.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, chunkError: false, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, chunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    // A chunk that failed to load is a deploy/network problem, not a bug in
    // the page: reload to pick up the current build (once per cooldown) and
    // only report it when the reload is not attempted.
    if (isChunkLoadError(error)) {
      if (reloadOnceForChunkError()) {
        this.setState({ reloading: true });
        return;
      }
      reportError(error, { source: 'chunk_load_error', componentStack: errorInfo?.componentStack });
      return;
    }
    reportError(error, {
      source: 'react_error_boundary',
      componentStack: errorInfo?.componentStack,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const { chunkError, reloading } = this.state;
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {chunkError ? 'Updating the portal' : 'Something went wrong'}
            </h1>
            <p className="text-gray-600 mb-6">
              {reloading
                ? 'A new version is available. Reloading now…'
                : chunkError
                  ? 'The portal was updated while this page was open. Please reload to continue.'
                  : 'Sorry about that — an unexpected error occurred. Reloading the page usually fixes it.'}
            </p>
            {!reloading && (
              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-gray-900 text-white font-medium hover:bg-gray-700 transition-colors"
              >
                Reload page
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
